// Vendor imports
const _ = require('lodash')
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')

const POSITION_THROTTLE_FREQUENCY = 66 // ~15 frames per second

class CirclePositioner extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = { offset: null }
    this.ref = React.createRef()

    this.recalculatePosition = _.throttle(
      this.recalculatePosition.bind(this),
      POSITION_THROTTLE_FREQUENCY
    )
  }

  componentDidMount() {
    // Requires getBoundingClientRect which is not available server-side
    if (typeof window !== 'undefined') {
      this.recalculateAngle()
      this.recalculatePosition()
      window.addEventListener('resize', this.recalculatePosition)
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.recalculatePosition)
      this.recalculatePosition.cancel()
    }
  }

  componentDidUpdate(prevProps) {
    if (typeof window !== 'undefined') {
      if (prevProps.angle !== this.props.angle) {
        this.recalculateAngle()
        this.recalculatePosition()
      }
    }
  }

  recalculateAngle() {
    const angleInDegrees = this.props.angle - 90
    const angleInRadians = angleInDegrees * (Math.PI / 180)
    this.angleSine = Math.sin(angleInRadians)
    this.angleCosine = Math.cos(angleInRadians)
  }

  recalculatePosition() {
    const el = this.ref.current
    // Sanity check that the ref is in the DOM
    if (!el || !el.parentElement) {
      this.setState(() => ({ offset: null }))
      return
    }
    const parentRect = el.parentElement.getBoundingClientRect()
    // Sanity check on the parent dimensions
    if (parentRect.width !== parentRect.height) {
      this.setState(() => ({ offset: null }))
      return
    }
    const parentRadius = parentRect.width / 2
    // cos(x) = adjacent / hypotenuse
    const adjacentLength = this.angleCosine * parentRadius
    // sin(x) = opposite / hypotenuse
    const oppositeLength = this.angleSine * parentRadius
    // Determine offset based on center of the parent and the ref
    const refRect = el.getBoundingClientRect()
    this.setState({
      offset: {
        x: adjacentLength + parentRadius - (refRect.width / 2),
        y: oppositeLength + parentRadius - (refRect.height / 2)
      }
    })
  }

  positionedStyle() {
    const { offset } = this.state
    if (offset) {
      return {
        position: 'absolute',
        top: `${Math.round(offset.y)}px`,
        right: 'auto',
        bottom: 'auto',
        left: `${Math.round(offset.x)}px`
      }
    }
  }

  render() {
    return React.createElement('div', {
      ref: this.ref,
      className: classNames(
        this.props.className,
        'CirclePositioner'
      ),
      style: this.positionedStyle()
    },
      this.props.children
    )
  }
}

CirclePositioner.propTypes = {
  angle: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node
}

CirclePositioner.defaultProps = {
  angle: 135 // degrees from the top of the circle
}

module.exports = CirclePositioner
